import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiRequest, HttpMethod } from '@api/common';
import { queryKeys, showApiError } from '@api/queryClient';
import toastService from '@services/toastService';

// ============================================================================
// Types
// ============================================================================

export interface TankCustomer {
  customerId?: string;
  customerName?: string;
}

export interface SimpleProduct {
  id: string;
  code: string;
  name: string;
  density: string;
  notes: string;
  isEnabled: boolean;
}

export interface TankBalance {
  tankId: string;
  timestamp: string;
  product: SimpleProduct;
  weight: number;
  weightCapasity: number;
  volume: number;
  volumeCapasity: number;
  fillPercentage: number;
}

export interface TankSurfaceLevel {
  tankId: string;
  timestamp: string;
  level: number;
  temperature?: number;
  weight: number;
  weightCapasity: number;
  volume: number;
  volumeCapasity: number;
  fillPercentage: number;
}

export enum FlowMeterType {
  None = 0,
  Normal = 1,
  HydrogenPeroxide = 2
}

export interface Tank {
  id?: string;
  name: string;
  capacity: number;
  baseLevel?: number;
  levelFactor?: number;
  customers?: TankCustomer[];
  notes: string;
  terminalId?: string;
  terminalName?: string;
  productId?: string;
  productName?: string;
  isEnabled: boolean;
  balance?: TankBalance;
  surfaceLevel?: TankSurfaceLevel;
  flowMeterType: FlowMeterType;
}

export interface AddTankCmd {
  tankName: string;
  capacity: number;
  baseLevel?: number;
  levelFactor?: number;
  customerIds?: string[];
  notes: string;
  terminalId?: string;
  productId?: string;
  flowMeterType: FlowMeterType;
}

export interface UpdateTankCmd extends AddTankCmd {
  id?: string;
}

// ============================================================================
// API Functions
// ============================================================================

const API_ENDPOINT = 'tank';

const fetchTanks = async (): Promise<Tank[]> => {
  return apiRequest<void, Tank[]>(HttpMethod.GET, API_ENDPOINT);
};

const fetchTank = async (id: string): Promise<Tank> => {
  return apiRequest<void, Tank>(HttpMethod.GET, `${API_ENDPOINT}/${id}`);
};

const fetchTankBalances = async (): Promise<TankBalance[]> => {
  return apiRequest<void, TankBalance[]>(HttpMethod.GET, `${API_ENDPOINT}/balances`);
};

const fetchTankSurfaceLevels = async (): Promise<TankSurfaceLevel[]> => {
  return apiRequest<void, TankSurfaceLevel[]>(HttpMethod.GET, `${API_ENDPOINT}/surfacelevels`);
};

const createTank = async (tank: AddTankCmd): Promise<Tank> => {
  return apiRequest<AddTankCmd, Tank>(HttpMethod.POST, API_ENDPOINT, tank);
};

const updateTank = async (tank: UpdateTankCmd): Promise<Tank> => {
  return apiRequest<UpdateTankCmd, Tank>(HttpMethod.PUT, API_ENDPOINT, tank);
};

const deleteTank = async (tankId: string): Promise<void> => {
  return apiRequest<void, void>(HttpMethod.DELETE, `${API_ENDPOINT}/${tankId}`);
};

// ============================================================================
// Query Hooks
// ============================================================================

/**
 * Fetch all tanks
 */
export const useTanksQuery = () => {
  return useQuery({
    queryKey: queryKeys.tanks.all,
    queryFn: fetchTanks,
  });
};

/**
 * Fetch a single tank by ID
 */
export const useTankQuery = (id: string | undefined) => {
  return useQuery({
    queryKey: queryKeys.tanks.detail(id!),
    queryFn: () => fetchTank(id!),
    enabled: !!id,
  });
};

/**
 * Fetch tank balances
 */
export const useTankBalancesQuery = () => {
  return useQuery({
    queryKey: queryKeys.tanks.balances(),
    queryFn: fetchTankBalances,
  });
};

/**
 * Fetch tank surface levels
 */
export const useTankSurfaceLevelsQuery = () => {
  return useQuery({
    queryKey: queryKeys.tanks.surfaceLevels(),
    queryFn: fetchTankSurfaceLevels,
  });
};

// ============================================================================
// Mutation Hooks
// ============================================================================

/**
 * Create a new tank
 */
export const useCreateTankMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createTank,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tanks.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.tanks.balances() });
      toastService.success({ title: 'save_succeeded' });
    },
    onError: (error: unknown) => {
      showApiError(error, 'save_failed');
    },
  });
};

/**
 * Update an existing tank
 */
export const useUpdateTankMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateTank,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tanks.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.tanks.balances() });
      toastService.success({ title: 'save_succeeded' });
    },
    onError: (error: unknown) => {
      showApiError(error, 'save_failed');
    },
  });
};

/**
 * Delete a tank
 */
export const useDeleteTankMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteTank,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tanks.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.tanks.balances() });
      toastService.success({ title: 'tank.tank_removed' });
    },
    onError: (error: unknown) => {
      showApiError(error, 'save_failed');
    },
  });
};

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Combine tanks with their balances and surface levels
 * This replicates the behavior of the Redux tanksListSelector
 */
export const combineTanksWithBalances = (
  tanks: Tank[],
  balances: TankBalance[] | undefined,
  surfaceLevels: TankSurfaceLevel[] | undefined
): Tank[] => {
  const balancesById = balances?.reduce((acc, b) => {
    acc[b.tankId] = b;
    return acc;
  }, {} as Record<string, TankBalance>) ?? {};

  const surfaceLevelsById = surfaceLevels?.reduce((acc, s) => {
    acc[s.tankId] = s;
    return acc;
  }, {} as Record<string, TankSurfaceLevel>) ?? {};

  return tanks.map(tank => ({
    ...tank,
    balance: tank.id ? balancesById[tank.id] ?? null : null,
    surfaceLevel: tank.id ? surfaceLevelsById[tank.id] ?? null : null,
  }));
};

// ============================================================================
// Historical Tank Customers Query
// ============================================================================

export interface HistoricalTankCustomer {
  customerId: string;
  customerName: string;
}

const fetchTankCustomers = async (tankId: string, from?: string): Promise<HistoricalTankCustomer[]> => {
  const url = from
    ? `${API_ENDPOINT}/${tankId}/customers?from=${from}`
    : `${API_ENDPOINT}/${tankId}/customers`;
  return apiRequest<void, HistoricalTankCustomer[]>(HttpMethod.GET, url);
};

/**
 * Fetch historical customers for a tank (customers who have had bookkeeping entries)
 * @param tankId - The tank ID to fetch customers for
 * @param from - Optional ISO date string to filter customers from this date onwards
 */
export const useTankCustomersQuery = (tankId: string | undefined, from?: string) => {
  return useQuery({
    queryKey: queryKeys.tanks.list({ tankId, from, type: 'customers' }),
    queryFn: () => fetchTankCustomers(tankId!, from),
    enabled: !!tankId,
  });
};
